import React, { useEffect, useState, useCallback } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
// Relative, not "@/...": resolved for real under Jest, like lib/validation below.
import { apiErrorCode } from "../lib/apiErrors";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, UserPlus, Shield, Trash2, Pause, Play, X } from "lucide-react";
// Relative, not "@/...": validation infrastructure, resolved for REAL under Jest (no "@"
// alias there) — see lib/validation.js / hooks/useFieldErrors.js headers.
import { emailField } from "../lib/validation";
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { useOperator } from "./OperatorContext";
import { useScreenWrites } from "./screenWrites";

const INVITE_LOCALES = ["fr", "en", "es"];

// `locale: null` means "follow the UI language": the invitation language defaults to whatever
// the operator's UI is in WHEN they send, until they pick one for this member explicitly.
const EMPTY_INVITE = { email: "", name: "", role: "viewer", locale: null };

const inviteSchema = z.object({ email: emailField });

// Team-change refusals the server names by code (super_admin_team_routes.py), each said in
// the operator's language rather than as the server's English message.
const ROSTER_REFUSALS = {
  LAST_TEAM_ADMINISTRATOR: "team.lastAdministrator",
  TEAM_ROSTER_BUSY: "team.rosterBusy",
};

const ROLE_BADGES = {
  super_admin: "bg-rose-100 text-rose-700 border-rose-200",
  finance: "bg-amber-100 text-amber-700 border-amber-200",
  support: "bg-emerald-100 text-emerald-700 border-emerald-200",
  viewer: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function TeamManager() {
  const t = useT("superadmin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { fieldErrors, validate, clearFieldError, clearAllErrors } = useFieldErrors();
  const [team, setTeam] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const { me } = useOperator();
  const { manage: canWrite } = useScreenWrites("team");
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState(EMPTY_INVITE);
  const inviteLocale = form.locale ?? lang;
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tm, c] = await Promise.all([
        api.get("/super-admin/team"),
        api.get("/super-admin/rbac/catalog"),
      ]);
      setTeam(tm.data);
      setCatalog(c.data);
    } catch (e) {
      toast.error(t("team.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (validate(inviteSchema, form)) { toast.error(tc("validation.fixHighlighted")); return; }
    try {
      const res = await api.post("/super-admin/team", { ...form, locale: inviteLocale });
      // The member exists either way. Only `sent` says the mail went out, and only `queued`
      // has an outbox row that is retried automatically; `not_sent` (or a verdict this screen
      // does not know) must not promise a retry that does not exist.
      const verdict = res.data?.invite_email;
      const email = { email: form.email };
      if (verdict === "sent") toast.success(t("team.memberAddedEmailed", email));
      else toast.warning(t(verdict === "queued" ? "team.memberAddedEmailPending" : "team.memberAddedEmailNotSent", email));
      setForm(EMPTY_INVITE);
      setInviting(false);
      load();
    } catch (err) {
      toast.error(extractApiError(err, t("team.error"), t));
    }
  };

  const changeError = (err) => {
    const refusal = ROSTER_REFUSALS[apiErrorCode(err)];
    return refusal ? t(refusal) : extractApiError(err, t("team.error"), t);
  };

  const update = async (user_id, patch) => {
    try {
      await api.put(`/super-admin/team/${user_id}`, patch);
      toast.success(t("team.updated"));
      load();
    } catch (err) {
      toast.error(changeError(err));
    }
  };

  // An operator by configuration cannot lose its row: without one, the server configuration
  // would make it a Super-Admin again. The server keeps it suspended (`outcome: "suspended"`),
  // and the operator is told so before and after.
  const remove = async (member) => {
    const { user_id, email } = member;
    const question = member.by_configuration ? "team.confirmRemoveConfigured" : "team.confirmRemove";
    if (!window.confirm(t(question, { email }))) return;
    try {
      const res = await api.delete(`/super-admin/team/${user_id}`);
      if (res.data?.outcome === "suspended") toast.success(t("team.memberRemovedKeptSuspended", { email }));
      else toast.success(t("team.memberRemoved"));
      load();
    } catch (err) {
      toast.error(changeError(err));
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;

  // Role names come from the dictionary by role key; the server's `label`/`role_label` is English.
  const roleName = (key) => t(`team.role.${key}`, undefined, key);

  return (
    <div data-testid="sa-team">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("team.title")}</h1>
          <p className="text-slate-500">{t("team.subtitle")}</p>
        </div>
        {canWrite && (
          <Button
            onClick={() => setInviting(true)}
            data-testid="invite-member-btn"
            className="bg-amber-300 text-black hover:bg-amber-200"
          >
            <UserPlus className="w-4 h-4 mr-2" /> {t("team.invite")}
          </Button>
        )}
      </div>

      {inviting && (
        <form onSubmit={invite} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-sol font-semibold text-lg">{t("team.newMember")}</h3>
            <button
              type="button"
              data-testid="invite-close"
              aria-label={tc("actions.close")}
              onClick={() => { setInviting(false); setForm(EMPTY_INVITE); clearAllErrors(); }}
              className="text-slate-400 hover:text-slate-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Input
                data-testid="invite-email"
                placeholder="email@solunary.health"
                type="email"
                value={form.email}
                onChange={(e) => { setForm({ ...form, email: e.target.value }); clearFieldError("email"); }}
                className={`bg-white border-slate-200 text-slate-800 ${fieldErrors.email ? invalidInputClass : ""}`}
              />
              <FieldError message={fieldErrors.email ? tc(fieldErrors.email) : ""} testId="invite-email-error" />
            </div>
            <Input
              data-testid="invite-name"
              placeholder={t("team.phName")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-white border-slate-200 text-slate-800"
            />
            <select
              data-testid="invite-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-800"
            >
              {(catalog?.roles || []).map((r) => (
                <option key={r.key} value={r.key} className="bg-white">
                  {roleName(r.key)}
                </option>
              ))}
            </select>
            <label className="block text-xs text-slate-500">
              {t("team.inviteLocale")}
              <select
                data-testid="invite-locale"
                value={inviteLocale}
                onChange={(e) => setForm({ ...form, locale: e.target.value })}
                className="mt-1 w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-800"
              >
                {INVITE_LOCALES.map((l) => (
                  <option key={l} value={l} className="bg-white">{t(`team.inviteLocale.${l}`)}</option>
                ))}
              </select>
            </label>
          </div>
          <Button type="submit" className="mt-4 bg-amber-300 text-black hover:bg-amber-200" data-testid="invite-submit">
            {t("team.add")}
          </Button>
        </form>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-6 py-3">{t("team.colMember")}</th>
              <th className="text-left px-6 py-3">{t("team.colRole")}</th>
              <th className="text-left px-6 py-3">{t("team.colStatus")}</th>
              <th className="text-left px-6 py-3">{t("team.colPermissions")}</th>
              <th className="text-right px-6 py-3">{t("team.colActions")}</th>
            </tr>
          </thead>
          <tbody>
            {team.map((u) => (
              <tr key={u.user_id} className="border-t border-slate-100" data-testid={`team-row-${u.email}`}>
                <td className="px-6 py-4">
                  <div className="font-medium">{u.name || u.email.split("@")[0]}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                  {u.by_configuration === true && (
                    <span
                      className="mt-1 inline-flex text-[10px] rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600"
                      title={t("team.byConfigurationHint")}
                      data-testid={`config-badge-${u.email}`}
                    >
                      {t("team.byConfiguration")}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {canWrite && u.email !== me?.email ? (
                    <select
                      value={u.role}
                      onChange={(e) => update(u.user_id, { role: e.target.value })}
                      className={`text-xs rounded-full border px-3 py-1 ${ROLE_BADGES[u.role] || ROLE_BADGES.viewer}`}
                      data-testid={`role-select-${u.email}`}
                    >
                      {(catalog?.roles || []).map((r) => (
                        <option key={r.key} value={r.key} className="bg-white">{roleName(r.key)}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-3 py-1 ${ROLE_BADGES[u.role] || ROLE_BADGES.viewer}`}>
                      <Shield className="w-3 h-3" /> {roleName(u.role)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1 ${u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {t(`team.status.${u.status}`, undefined, u.status)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-xs text-slate-400">{t("team.permissionsCount", { n: u.permissions?.length || 0 })}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  {canWrite && u.email !== me?.email && (
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => update(u.user_id, { status: u.status === "active" ? "suspended" : "active" })}
                        data-testid={`toggle-status-${u.email}`}
                        className="text-slate-500 hover:text-slate-800"
                      >
                        {u.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => remove(u)}
                        data-testid={`delete-member-${u.email}`}
                        className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                  {u.email === me?.email && <span className="text-xs text-slate-400">{t("team.itsYou")}</span>}
                </td>
              </tr>
            ))}
            {team.length === 0 && (
              <tr><td colSpan="5" className="px-6 py-10 text-center text-slate-400">{t("team.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {catalog && (
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
          <h3 className="font-sol font-semibold text-lg mb-4">{t("team.rolesPerms")}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {catalog.roles.map((r) => (
              <div key={r.key} className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <div className={`inline-flex text-xs rounded-full border px-2.5 py-1 mb-3 ${ROLE_BADGES[r.key] || ROLE_BADGES.viewer}`}>
                  {roleName(r.key)}
                </div>
                <div className="text-xs text-slate-400">{t("team.permissionsCount", { n: r.permissions.length })}</div>
                <div className="mt-2 text-[10px] text-slate-600 max-h-32 overflow-y-auto leading-relaxed">
                  {r.permissions.map((p) => t(`team.perm.${p}`, undefined, p)).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
